import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Building2, X } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getBusinessDisplayName } from '../utils/businessSearch';
import { t } from '../utils/translations';

const STORAGE_KEY = 'cep-seen-business-ids-v1';
const DISMISS_KEY = 'cep-new-business-banner-dismissed';

function readSeenIds() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeSeenIds(ids) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Informe les utilisateurs dès qu’une nouvelle entreprise apparaît dans l’annuaire public.
 * (Après validation admin — les fiches pending ne sont pas visibles.)
 */
const NewBusinessAlert = () => {
  const { validatedBusinesses } = useBusiness();
  const { language } = useLanguage();
  const [banner, setBanner] = useState(null);

  const newestPublic = useMemo(() => {
    if (!validatedBusinesses?.length) return [];
    return [...validatedBusinesses].sort((a, b) => {
      const timeOf = (business) => {
        const stamp = business.approvedAt || business.createdAt;
        if (!stamp) return 0;
        if (stamp.toDate) return stamp.toDate().getTime();
        return new Date(stamp).getTime() || 0;
      };
      return timeOf(b) - timeOf(a);
    });
  }, [validatedBusinesses]);

  useEffect(() => {
    if (!newestPublic.length) return;

    const currentIds = newestPublic.map((b) => b.id).filter(Boolean);
    const seen = readSeenIds();

    // Première visite : mémoriser sans alerter
    if (seen == null) {
      writeSeenIds(currentIds);
      return;
    }

    const seenSet = new Set(seen);
    const newcomers = newestPublic.filter((b) => b.id && !seenSet.has(b.id));
    writeSeenIds(Array.from(new Set([...seen, ...currentIds])));

    if (!newcomers.length) return;

    const lead = newcomers[0];
    const name =
      getBusinessDisplayName(lead) ||
      (language === 'en' ? 'A new business' : 'Une nouvelle entreprise');
    const count = newcomers.length;

    toast.success(
      count === 1
        ? t(language, 'newBusinessAlert.toastSingle', { name })
        : t(language, 'newBusinessAlert.toastMultiple', { count, name }),
      {
        icon: '🏢',
        duration: 6000,
        id: 'new-business-alert'
      }
    );

    if (!sessionStorage.getItem(DISMISS_KEY)) {
      setBanner({
        count,
        name,
        category: lead.category,
        businessId: lead.id
      });
    }
  }, [newestPublic, language]);

  const dismissBanner = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setBanner(null);
  };

  const href = banner?.category ? `/category/${banner.category}` : '/categories';

  return (
    <AnimatePresence>
      {banner ? (
        <motion.div
          className="new-business-alert"
          initial={{ y: -24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -16, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          role="status"
          aria-live="polite"
        >
          <div className="new-business-alert__inner">
            <span className="new-business-alert__icon" aria-hidden="true">
              <Building2 size={18} />
            </span>
            <div className="new-business-alert__copy">
              <strong>
                {banner.count === 1
                  ? t(language, 'newBusinessAlert.bannerSingle', { name: banner.name })
                  : t(language, 'newBusinessAlert.bannerMultiple', {
                      count: banner.count,
                      name: banner.name
                    })}
              </strong>
              <span>{t(language, 'newBusinessAlert.bannerHint')}</span>
            </div>
            <Link to={href} className="new-business-alert__cta" onClick={dismissBanner}>
              {t(language, 'newBusinessAlert.cta')}
            </Link>
            <button
              type="button"
              className="new-business-alert__close"
              onClick={dismissBanner}
              aria-label={language === 'en' ? 'Close' : 'Fermer'}
            >
              <X size={16} />
            </button>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default NewBusinessAlert;
